import { Button, HStack, Input, MenuItem } from '@chakra-ui/react';
import { JSONContent } from '@tiptap/react';
import { SquarePlay } from 'lucide-react';
import { useState } from 'react';
import { Node, useReactFlow } from 'reactflow';
import { assignPersonaId, insertMediaIntoNode } from '../../tiptap/contentJSONs';
import { AnchoredPanel, AnchorRect } from './AnchoredPanel';

const PANEL_WIDTH = 320;

// The trigger and the panel are split so the panel can render OUTSIDE the
// add-block menu's MenuList, which closes (and, being lazy, unmounts) on select
// and would otherwise tear a panel rendered inside it straight back down.

export function YoutubeMenuItem({ onOpen }: { onOpen: () => void }) {
  return (
    <MenuItem onClick={onOpen} icon={<SquarePlay size="16px" />}>
      YouTube embed
    </MenuItem>
  );
}

type Props = {
  targetNode: Node;
  getTargetNodeId?: () => void;
  isOpen: boolean;
  onClose: () => void;
  anchorRect: AnchorRect | null;
};

function YoutubeMenu({ targetNode, getTargetNodeId, isOpen, onClose, anchorRect }: Props) {
  const { setNodes } = useReactFlow();
  const [input, setInput] = useState('');

  const createJSONContent = (): JSONContent => {
    return {
      type: 'resizableMedia',
      attrs: {
        src: input,
        'media-type': 'youtube',
      },
    };
  };

  const addYoutubeVideo = () => {
    const block = assignPersonaId(createJSONContent());
    const targetNodeId = targetNode?.id || getTargetNodeId?.();

    if (!targetNodeId) {
      throw new Error('No target node id');
    }

    const finish = () => {
      setInput('');
      onClose();
    };

    if (insertMediaIntoNode(setNodes, targetNodeId, block)) {
      finish();
      return;
    }

    // The target node isn't the live (selected) one, so it has no mounted
    // editor. Select it so its editor mounts, then insert on the next frame.
    setNodes((nodes) => nodes.map((node) => ({ ...node, selected: node.id === targetNodeId })));
    requestAnimationFrame(() => {
      insertMediaIntoNode(setNodes, targetNodeId, block);
      finish();
    });
  };

  return (
    <AnchoredPanel
      isOpen={isOpen}
      onClose={onClose}
      anchorRect={anchorRect}
      width={PANEL_WIDTH}
      title="YouTube embed"
    >
      <HStack>
        <Input
          autoFocus
          size="sm"
          onChange={(e) => setInput(e.target.value)}
          value={input}
          placeholder="Paste a YouTube video URL"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && input) {
              addYoutubeVideo();
            }
          }}
        />
        <Button isDisabled={!input} size="sm" onClick={addYoutubeVideo}>
          Add
        </Button>
      </HStack>
    </AnchoredPanel>
  );
}

export default YoutubeMenu;
